// client/src/coms/components/SoundboardManager.js
// Settings → Soundboard: manage sound packs (the soundboard's collections) without joining voice.
// Same API and collections as the voice-channel SoundboardPanel: personal packs, packs installed from
// share codes or files, server packs (editing needs Manage soundboard; the server enforces it), and the
// clips saved on this device. Installing or importing someone else's pack shows the trust warning first.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comsApiDelete, comsApiGet, comsApiPatch, comsApiPost, comsApiPut } from '../comsApi';
import { isProbablyAnimated, prepareImageFile, uploadMultipart } from '../imagePrep';
import { sha256Hex } from '../soundboard/audioPrep';
import { deleteLocalSound, listLocalSounds, saveLocalSound, updateLocalSound } from '../soundboard/soundStore';
import SoundClipEditor from './SoundClipEditor';
import TrustDialog from './TrustDialog';

const LOCAL = 'local';
const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '7px 10px', fontSize: 13 };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export function formatDuration(ms) {
  const s = Math.max(0, Number(ms) || 0) / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}

export function visibilityLabel(c) {
  if (c.is_server || c.visibility === 'server') return c.server_name ? `Server · ${c.server_name}` : 'Server';
  if (c.visibility === 'public') return c.published ? 'Marketplace' : 'Public';
  if (c.visibility === 'unlisted') return 'Share code';
  if (c.visibility === 'friends') return 'Friends';
  return 'Only me';
}

const CATEGORY_LABEL = { memes: 'Memes', reactions: 'Reactions', music: 'Music', gaming: 'Gaming', effects: 'Sound effects', voices: 'Voices', animals: 'Animals', ambience: 'Ambience', other: 'Other' };

// Sidebar sections. Server packs are grouped per server (mine included); installed = someone else's.
export function groupPacks(collections) {
  const mine = [];
  const installed = [];
  const servers = new Map();
  for (const c of collections || []) {
    if (c.is_server || c.visibility === 'server') {
      const key = c.server_key || String(c.server_id || '');
      if (!servers.has(key)) servers.set(key, { key, name: c.server_name || 'Server', packs: [] });
      servers.get(key).packs.push(c);
    } else if (c.is_mine) mine.push(c);
    else installed.push(c);
  }
  return { mine, installed, servers: Array.from(servers.values()).sort((a, b) => a.name.localeCompare(b.name)) };
}

function PackArt({ pack, size = 36, emoji }) {
  const style = { width: size, height: size, borderRadius: Math.round(size / 4), flex: '0 0 auto', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(var(--vx-accent-rgb, 88,101,242), 0.55), rgba(var(--vx-link-rgb, 141,180,255), 0.25))', fontSize: Math.round(size * 0.5) };
  if (pack && pack.icon_url) return <img src={pack.icon_url} alt="" style={{ ...style, objectFit: 'cover', background: 'var(--vxs-1, #111214)' }} />;
  return (
    <span aria-hidden="true" style={style}>
      {emoji || '🔊'}
    </span>
  );
}

function Chip({ children, tone }) {
  const tones = {
    warn: { background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.45)', color: '#fcd34d' },
    info: { background: 'rgba(var(--vx-accent-rgb, 88,101,242), 0.18)', border: '1px solid rgba(var(--vx-accent-rgb, 88,101,242), 0.45)', color: '#c7ceff' },
  };
  return <span className="vox-sbm-chip" style={tones[tone] || undefined}>{children}</span>;
}

export default function SoundboardManager() {
  const [collections, setCollections] = useState([]);
  const [servers, setServers] = useState([]);
  const [personalQuota, setPersonalQuota] = useState(null);
  const [categories, setCategories] = useState(Object.keys(CATEGORY_LABEL));
  const [publishing, setPublishing] = useState(null); // { id, category, tags }
  const [describing, setDescribing] = useState(null); // pack description being edited
  const [localSounds, setLocalSounds] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(LOCAL);
  const [query, setQuery] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [trust, setTrust] = useState(null);
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWhere, setNewWhere] = useState('private');
  const [renaming, setRenaming] = useState(null);
  const [editor, setEditor] = useState(null); // { replace?: { kind, sound } }
  const [editingSound, setEditingSound] = useState(null); // { id, name, emoji }
  const [confirm, setConfirm] = useState(''); // key of the destructive action awaiting confirmation
  const [playing, setPlaying] = useState('');
  const audioRef = useRef(null);
  // Debounced volume saves: key -> { timer, run } while waiting, key -> promise while in flight.
  const volTimers = useRef({});
  const volSaves = useRef({});

  // Sends waiting volume saves now and waits for those in flight, so a reload never shows (or a later
  // edit never re-saves) a volume the server has not stored yet.
  const flushVolumes = useCallback(() => {
    Object.values(volTimers.current).forEach(({ timer, run }) => {
      clearTimeout(timer);
      run();
    });
    volTimers.current = {};
    return Promise.all(Object.values(volSaves.current));
  }, []);

  const load = useCallback(async () => {
    await flushVolumes();
    try {
      setLocalSounds(await listLocalSounds());
    } catch {}
    try {
      const r = await comsApiGet('/sounds/collections?all_servers=1');
      setCollections(Array.isArray(r && r.collections) ? r.collections : []);
      setServers(Array.isArray(r && r.servers) ? r.servers : []);
      setPersonalQuota(r && r.personal_quota ? r.personal_quota : null);
      if (r && r.limits && Array.isArray(r.limits.categories)) setCategories(r.limits.categories);
    } catch (e) {
      setErr(errText(e, 'Could not load your sound packs.'));
    } finally {
      setLoaded(true);
    }
  }, [flushVolumes]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause();
      flushVolumes(); // leaving the tab right after a drag still saves it
    },
    [flushVolumes]
  );

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  // Keep the selection valid after a delete / uninstall.
  useEffect(() => {
    if (loaded && selected !== LOCAL && !collections.some((c) => String(c.id) === String(selected))) setSelected(LOCAL);
  }, [loaded, selected, collections]);

  async function act(fn, fallback) {
    setBusy(true);
    setErr('');
    try {
      const out = await fn();
      await load();
      return out;
    } catch (e) {
      setErr(errText(e, fallback));
      return null;
    } finally {
      setBusy(false);
      setConfirm('');
    }
  }

  function select(key) {
    setSelected(key);
    setEditor(null);
    setEditingSound(null);
    setRenaming(null);
    setConfirm('');
    setErr('');
  }

  const groups = useMemo(() => groupPacks(collections), [collections]);
  const editable = useMemo(() => collections.filter((c) => c.can_edit), [collections]);
  const manageable = useMemo(() => servers.filter((s) => s.can_manage), [servers]);
  const pack = selected === LOCAL ? null : collections.find((c) => String(c.id) === String(selected)) || null;
  const q = query.trim().toLowerCase();
  const soundMatch = (s) => !q || String(s.name || '').toLowerCase().includes(q);
  const packMatch = (c) => !q || String(c.name || '').toLowerCase().includes(q) || (c.sounds || []).some(soundMatch);

  // ---- playback preview (local only, never sent to a channel) ----
  function preview(key, src, volume) {
    if (audioRef.current) audioRef.current.pause();
    if (playing === key) {
      setPlaying('');
      return;
    }
    const isBlob = typeof src !== 'string';
    const url = isBlob ? URL.createObjectURL(src) : src;
    const a = new Audio(url);
    a.volume = Math.max(0, Math.min(1, Number(volume) || 1));
    audioRef.current = a;
    setPlaying(key);
    const done = () => {
      if (isBlob) URL.revokeObjectURL(url);
      setPlaying((cur) => (cur === key ? '' : cur));
    };
    a.onended = done;
    a.onerror = () => {
      done();
      setErr('Could not play that sound.');
    };
    a.play().catch(done);
  }

  // ---- packs ----
  async function createPack() {
    const body = { name: newName.trim() };
    if (newWhere === 'friends') body.visibility = 'friends';
    else if (newWhere.startsWith('server:')) Object.assign(body, { visibility: 'server', server_key: newWhere.slice(7) });
    const r = await act(() => comsApiPost('/sounds/collections', body), 'Could not create the pack.');
    if (r && r.collection) {
      setCreating(false);
      setNewName('');
      setNewWhere('private');
      select(r.collection.id);
      setNotice(`Created “${r.collection.name}”.`);
    }
  }

  async function rename() {
    const r = await act(() => comsApiPatch(`/sounds/collections/${pack.id}`, { name: renaming }), 'Could not rename the pack.');
    if (r) setRenaming(null);
  }

  const setArtwork = (file) =>
    act(async () => {
      const prepared = await prepareImageFile(file, { size: 256, mode: 'crop' });
      await uploadMultipart(`/sounds/collections/${pack.id}/icon`, {}, isProbablyAnimated(file) ? file : prepared);
    }, 'Could not set the artwork.');

  async function exportPack(c) {
    setErr('');
    try {
      const res = await fetch(`/api/coms/sounds/collections/${c.id}/export`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed.');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await res.blob());
      a.download = `${c.name.replace(/[^A-Za-z0-9_-]+/g, '_') || 'sounds'}.voxsounds.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setErr(errText(e, 'Export failed.'));
    }
  }

  async function previewCode() {
    setErr('');
    const c0 = code.trim();
    try {
      const r = await comsApiGet(`/sounds/by-code/${encodeURIComponent(c0)}`);
      const c = r.collection;
      setTrust({
        title: 'Install sound pack?',
        info: { name: c.name, creator: c.creator, count: c.sound_count, bytes: c.total_bytes, types: c.file_types, permissions: 'None (audio clips only)' },
        run: () => comsApiPost(`/sounds/by-code/${encodeURIComponent(c0)}/install`, { consent: true }).then((x) => {
          setCode('');
          return x;
        }),
      });
    } catch (e) {
      setErr(errText(e, 'Pack not found or not shared with you.'));
    }
  }

  async function importFile(file) {
    setErr('');
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return setErr('That file is too large to be a sound pack.');
    let doc;
    try {
      doc = JSON.parse(await file.text());
    } catch {
      return setErr('That file is not a Voxemera sound pack.');
    }
    if (!doc || doc.format !== 'voxemera-sound-collection') return setErr('That file is not a Voxemera sound pack.');
    const sounds = Array.isArray(doc.sounds) ? doc.sounds : [];
    setTrust({
      title: 'Import sound pack?',
      info: {
        name: doc.name,
        creator: doc.creator ? `${doc.creator} (from a file)` : 'From a file',
        count: sounds.length,
        bytes: sounds.reduce((n, s) => n + Math.floor((String((s && s.data) || '').length * 3) / 4), 0),
        types: Array.from(new Set(sounds.map((s) => String((s && s.mime) || 'unknown')))),
        permissions: 'None (audio clips only)',
      },
      run: () => comsApiPost('/sounds/import', { consent: true, collection: doc }),
    });
  }

  async function runTrust() {
    const r = await act(() => trust.run(), 'Install failed.');
    setTrust(null);
    if (r && r.collection) {
      select(r.collection.id);
      setNotice(`Added “${r.collection.name}”.`);
    }
  }

  // ---- sounds ----
  function setSoundVolume(kind, s, volume) {
    // Update the slider at once; save after the user stops dragging.
    if (kind === LOCAL) setLocalSounds((list) => list.map((x) => (x.id === s.id ? { ...x, volume } : x)));
    else setCollections((list) => list.map((c) => ({ ...c, sounds: c.sounds.map((x) => (x.id === s.id ? { ...x, volume } : x)) })));
    const key = `${kind}:${s.id}`;
    const run = () => {
      delete volTimers.current[key];
      const save = Promise.resolve(kind === LOCAL ? updateLocalSound(s.id, { volume }) : comsApiPatch(`/sounds/sounds/${s.id}`, { volume }))
        .catch((e) => setErr(errText(e, 'Could not save the volume.')))
        .finally(() => {
          if (volSaves.current[key] === save) delete volSaves.current[key];
        });
      volSaves.current[key] = save;
    };
    if (volTimers.current[key]) clearTimeout(volTimers.current[key].timer);
    volTimers.current[key] = { timer: setTimeout(run, 400), run };
  }

  const saveSoundDetails = (kind, s) =>
    act(async () => {
      const patch = { name: editingSound.name.trim() || s.name, emoji: editingSound.emoji };
      if (kind !== LOCAL) Object.assign(patch, { description: editingSound.description || '', keywords: editingSound.keywords || '' });
      if (kind === LOCAL) await updateLocalSound(s.id, patch);
      else await comsApiPatch(`/sounds/sounds/${s.id}`, patch);
      setEditingSound(null);
    }, 'Could not save the sound.');

  const removeSound = (kind, s) =>
    act(async () => {
      if (kind === LOCAL) await deleteLocalSound(s.id);
      else await comsApiDelete(`/sounds/sounds/${s.id}`);
    }, 'Could not remove the sound.');

  // Move between packs (and this device). Device -> pack uploads the clip; pack -> device downloads it.
  const moveSound = (kind, s, to) =>
    act(async () => {
      if (kind === LOCAL) {
        const fd = new FormData();
        fd.append('name', s.name || 'Sound');
        fd.append('emoji', s.emoji || '');
        fd.append('volume', String(s.volume != null ? s.volume : 1));
        fd.append('file', s.wav, 'clip.wav');
        const res = await fetch(`/api/coms/sounds/collections/${to}/sounds`, { method: 'POST', credentials: 'include', body: fd });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error((data && data.error) || 'Upload failed');
        await deleteLocalSound(s.id);
      } else if (to === LOCAL) {
        const res = await fetch(s.url, { credentials: 'include' });
        if (!res.ok) throw new Error('Could not download the sound.');
        const wav = await res.blob();
        await saveLocalSound({ name: s.name, emoji: s.emoji, volume: s.volume, wav, localHash: await sha256Hex(new Uint8Array(await wav.arrayBuffer())), serverHash: s.hash });
        await comsApiDelete(`/sounds/sounds/${s.id}`);
      } else {
        await comsApiPatch(`/sounds/sounds/${s.id}`, { collection_id: Number(to) });
      }
      const dest = to === LOCAL ? 'this device' : (collections.find((c) => String(c.id) === String(to)) || {}).name || 'the pack';
      setNotice(`Moved “${s.name}” to ${dest}.`);
    }, 'Could not move the sound.');

  const copySound = (from, s, to) =>
    act(async () => {
      await comsApiPost(`/sounds/collections/${from.id}/copy`, { target_collection_id: Number(to), sound_ids: [s.id], consent: true });
      setNotice(`Copied “${s.name}”.`);
    }, 'Could not copy the sound.');

  const toggleFavorite = (kind, s) =>
    act(async () => {
      if (kind === LOCAL) await updateLocalSound(s.id, { favorite: !s.favorite });
      else await (s.favorite ? comsApiDelete(`/sounds/sounds/${s.id}/favorite`) : comsApiPut(`/sounds/sounds/${s.id}/favorite`, {}));
    }, 'Could not update favourites.');

  // ---- rendering ----
  const firstEmoji = (c) => ((c.sounds || []).find((s) => s.emoji) || {}).emoji;

  const packButton = (key, label, sub, art, extra) => (
    <button key={key} type="button" className={`vox-sbm-pack ${String(selected) === String(key) ? 'active' : ''}`} onClick={() => select(key)} aria-current={String(selected) === String(key) ? 'true' : undefined}>
      {art}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span className="vox-sbm-pack-name">{label}</span>
        <span className="vox-sbm-pack-sub">{sub}</span>
      </span>
      {extra}
    </button>
  );

  const packItem = (c) =>
    packButton(
      c.id,
      c.name,
      `${c.sound_count != null ? c.sound_count : c.sounds.length} sound${(c.sound_count != null ? c.sound_count : c.sounds.length) === 1 ? '' : 's'} · ${c.is_mine ? (c.visibility === 'server' ? 'Yours' : visibilityLabel(c)) : `by ${c.creator || 'someone'}`}`,
      <PackArt pack={c} emoji={firstEmoji(c)} />,
      c.updated_since_install ? <span className="vox-sbm-dot" title="Updated since you installed it" /> : !c.can_edit ? <span className="vox-sbm-lock" title="View only">🔒</span> : null
    );

  const section = (title, items, empty) => (
    <div className="vox-sbm-section">
      <div className="vox-sbm-section-title">{title}</div>
      {items.length ? items : empty ? <div className="vox-sbm-empty-small">{empty}</div> : null}
    </div>
  );

  const moveTargets = (kind, fromId) => [
    ...(kind === LOCAL ? [] : [{ value: LOCAL, label: 'This device' }]),
    ...editable.filter((c) => String(c.id) !== String(fromId)).map((c) => ({ value: String(c.id), label: c.visibility === 'server' ? `${c.name} (${c.server_name || 'server'})` : c.name })),
  ];

  function soundRow(kind, s, container) {
    const canEdit = kind === LOCAL || (container && container.can_edit);
    const key = `${kind}:${s.id}`;
    const isEditing = editingSound && editingSound.key === key;
    const targets = canEdit ? moveTargets(kind, container && container.id) : editable.filter((c) => !container || c.id !== container.id).map((c) => ({ value: String(c.id), label: c.name }));
    return (
      <div key={key} className="vox-sbm-sound">
        <button type="button" className={`vox-sbm-play ${playing === key ? 'on' : ''}`} onClick={() => preview(key, kind === LOCAL ? s.wav : s.url, s.volume)} aria-label={playing === key ? `Stop ${s.name}` : `Preview ${s.name}`} title="Preview (only you hear it)">
          {playing === key ? '■' : '▶'}
        </button>
        {isEditing ? (
          <span style={{ display: 'flex', gap: 6, flex: 1, minWidth: 180, flexWrap: 'wrap' }}>
            <input value={editingSound.emoji} onChange={(e) => setEditingSound({ ...editingSound, emoji: e.target.value.slice(0, 4) })} placeholder="🔔" aria-label="Emoji" style={{ ...field, width: 46, textAlign: 'center', padding: '5px 6px' }} />
            <input
              value={editingSound.name}
              onChange={(e) => setEditingSound({ ...editingSound, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveSoundDetails(kind, s);
                if (e.key === 'Escape') setEditingSound(null);
              }}
              maxLength={40}
              aria-label="Sound name"
              autoFocus
              style={{ ...field, flex: 1, minWidth: 100, padding: '5px 8px' }}
            />
            {kind !== LOCAL ? (
              <>
                <input value={editingSound.description || ''} onChange={(e) => setEditingSound({ ...editingSound, description: e.target.value.slice(0, 200) })} placeholder="Description" aria-label="Description" style={{ ...field, flex: '1 1 100%', padding: '5px 8px' }} />
                <input value={editingSound.keywords || ''} onChange={(e) => setEditingSound({ ...editingSound, keywords: e.target.value })} placeholder="Search keywords" aria-label="Search keywords" style={{ ...field, flex: '1 1 100%', padding: '5px 8px' }} />
              </>
            ) : null}
            <button type="button" className="coms-mini-btn tiny primary" disabled={busy} onClick={() => saveSoundDetails(kind, s)}>Save</button>
            <button type="button" className="coms-mini-btn tiny" onClick={() => setEditingSound(null)}>Cancel</button>
          </span>
        ) : (
          <span className="vox-sbm-sound-main">
            <span className="vox-sbm-sound-emoji" aria-hidden="true">{s.emoji || '🔊'}</span>
            <span style={{ minWidth: 0 }}>
              <span className="vox-sbm-sound-name">{s.name}</span>
              <span className="vox-sbm-sound-sub">
                {s.duration_ms ? formatDuration(s.duration_ms) : kind === LOCAL ? 'On this device' : ''}
                {s.shortcut ? ` · ${s.shortcut}` : ''}
                {s.keywords && s.keywords.length ? ` · ${s.keywords.join(' ')}` : ''}
                {s.description ? ` · ${s.description}` : ''}
              </span>
            </span>
          </span>
        )}
        {!isEditing ? (
          <span className="vox-sbm-sound-actions">
            <button type="button" className="vox-sbm-icon-btn" style={{ color: s.favorite ? '#fbbf24' : undefined }} onClick={() => toggleFavorite(kind, s)} aria-label={s.favorite ? 'Remove from favourites' : 'Add to favourites'} title={s.favorite ? 'Favourite' : 'Add to favourites'}>
              ★
            </button>
            {canEdit ? (
              <>
                <label className="vox-sbm-volume" title={`Volume ${Math.round((s.volume != null ? s.volume : 1) * 100)}%`}>
                  <span aria-hidden="true">🔉</span>
                  <input type="range" min={0} max={1} step={0.05} value={s.volume != null ? s.volume : 1} aria-label={`Volume of ${s.name}`} onChange={(e) => setSoundVolume(kind, s, Number(e.target.value))} />
                </label>
                <button type="button" className="coms-mini-btn tiny" onClick={() => setEditingSound({ key, name: s.name || '', emoji: s.emoji || '', description: s.description || '', keywords: (s.keywords || []).join(' ') })}>Edit</button>
                <button type="button" className="coms-mini-btn tiny" onClick={() => setEditor({ replace: { kind: kind === LOCAL ? 'local' : 'col', sound: s } })}>Trim</button>
              </>
            ) : null}
            {targets.length ? (
              <select
                value=""
                aria-label={canEdit ? `Move ${s.name} to` : `Copy ${s.name} to`}
                style={{ ...field, padding: '3px 6px', fontSize: 12, maxWidth: 140 }}
                disabled={busy}
                onChange={(e) => {
                  const to = e.target.value;
                  if (!to) return;
                  if (canEdit) moveSound(kind, s, to);
                  else copySound(container, s, to);
                }}
              >
                <option value="">{canEdit ? 'Move to…' : 'Copy to…'}</option>
                {targets.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            ) : null}
            {canEdit ? (
              confirm === `sound:${key}` ? (
                <>
                  <button type="button" className="coms-mini-btn tiny vox-sbm-danger" disabled={busy} onClick={() => removeSound(kind, s)}>Remove</button>
                  <button type="button" className="coms-mini-btn tiny" onClick={() => setConfirm('')}>Keep</button>
                </>
              ) : (
                <button type="button" className="vox-sbm-icon-btn" onClick={() => setConfirm(`sound:${key}`)} aria-label={`Remove ${s.name}`} title="Remove">
                  ✕
                </button>
              )
            ) : null}
          </span>
        ) : null}
      </div>
    );
  }

  const serverOf = (c) => servers.find((s) => s.key === c.server_key) || null;

  function detail() {
    if (selected === LOCAL) {
      const list = localSounds.filter(soundMatch);
      return (
        <>
          <div className="vox-sbm-head">
            <PackArt size={64} emoji="💻" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="vox-sbm-title">This device</div>
              <div className="vox-sbm-chips">
                <Chip>Yours</Chip>
                <Chip>Only on this device</Chip>
                <Chip>{localSounds.length} sound{localSounds.length === 1 ? '' : 's'}</Chip>
              </div>
              <div className="vox-sbm-hint">
                Clips saved in this browser. A clip is uploaded only when you play it in a voice channel, and is removed from the server after 30 days without use. Move a clip into a pack to share it or use it on other devices.
              </div>
            </div>
          </div>
          <div className="vox-sbm-actions">
            <button type="button" className="coms-mini-btn primary" onClick={() => setEditor({})}>+ Add sound</button>
          </div>
          {editor ? <SoundClipEditor key={editor.replace ? `r:${editor.replace.sound.id}` : 'new'} collections={collections} defaultTarget={LOCAL} replace={editor.replace || null} onCancel={() => setEditor(null)} onSaved={() => { setEditor(null); load(); setNotice('Sound saved.'); }} /> : null}
          <div className="vox-sbm-sounds">{list.length ? list.map((s) => soundRow(LOCAL, s, null)) : <div className="vox-sbm-empty">{q ? 'No sounds match your search.' : 'No sounds on this device yet. Add one from a file or record it with your microphone.'}</div>}</div>
        </>
      );
    }
    if (!pack) return <div className="vox-sbm-empty">{loaded ? 'Choose a pack.' : 'Loading…'}</div>;
    const c = pack;
    const srv = serverOf(c);
    const count = c.sound_count != null ? c.sound_count : c.sounds.length;
    const list = c.sounds.filter(soundMatch);
    return (
      <>
        <div className="vox-sbm-head">
          <div style={{ position: 'relative' }}>
            <PackArt pack={c} size={64} emoji={firstEmoji(c)} />
            {c.can_edit ? (
              <label className="vox-sbm-art-edit" title="Change artwork">
                ✎
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" aria-label="Change pack artwork" style={{ display: 'none' }} onChange={(e) => e.target.files && e.target.files[0] && setArtwork(e.target.files[0])} />
              </label>
            ) : null}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            {renaming != null ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  value={renaming}
                  onChange={(e) => setRenaming(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renaming.trim().length >= 2) rename();
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  maxLength={40}
                  autoFocus
                  aria-label="Pack name"
                  style={{ ...field, fontSize: 15, fontWeight: 700, flex: 1, minWidth: 140 }}
                />
                <button type="button" className="coms-mini-btn primary" disabled={busy || renaming.trim().length < 2} onClick={rename}>Save</button>
                <button type="button" className="coms-mini-btn" onClick={() => setRenaming(null)}>Cancel</button>
              </div>
            ) : (
              <div className="vox-sbm-title">
                {c.name}
                {c.can_edit ? (
                  <button type="button" className="vox-sbm-icon-btn" onClick={() => setRenaming(c.name)} aria-label="Rename pack" title="Rename">
                    ✎
                  </button>
                ) : null}
              </div>
            )}
            <div className="vox-sbm-chips">
              <Chip tone={c.is_mine ? 'info' : undefined}>{c.is_mine ? 'Yours' : `by ${c.creator || 'someone'}`}</Chip>
              <Chip>{visibilityLabel(c)}</Chip>
              <Chip>
                {count}/{c.max_sounds || 60} sounds
              </Chip>
              {c.quota ? (
                <Chip tone={c.quota.used >= c.quota.limit ? 'warn' : undefined}>
                  {c.quota.scope === 'server' ? `${c.quota.used} / ${c.quota.limit} server slots` : `${c.quota.used} / ${c.quota.limit} personal sounds`}
                </Chip>
              ) : null}
              {c.published ? <Chip tone="info">In the Marketplace · {c.install_count || 0} install{c.install_count === 1 ? '' : 's'}</Chip> : null}
              {c.category ? <Chip>{CATEGORY_LABEL[c.category] || c.category}</Chip> : null}
              {c.total_duration_ms ? <Chip>{formatDuration(c.total_duration_ms)} total</Chip> : null}
              <Chip>v{c.version}</Chip>
              {!c.can_edit ? <Chip tone="warn">View only</Chip> : null}
              {srv && !srv.enabled ? <Chip tone="warn">Soundboard off on this server</Chip> : null}
            </div>
            {describing != null && describing.id === c.id ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                <input value={describing.text} onChange={(e) => setDescribing({ ...describing, text: e.target.value.slice(0, 200) })} placeholder="Describe this pack" aria-label="Pack description" style={{ ...field, flex: 1, minWidth: 180 }} autoFocus />
                <button type="button" className="coms-mini-btn tiny primary" disabled={busy} onClick={() => act(() => comsApiPatch(`/sounds/collections/${c.id}`, { description: describing.text }).then(() => setDescribing(null)), 'Could not save the description.')}>Save</button>
                <button type="button" className="coms-mini-btn tiny" onClick={() => setDescribing(null)}>Cancel</button>
              </div>
            ) : c.description || c.can_edit ? (
              <div className="vox-sbm-hint">
                {c.description || 'No description yet.'}
                {c.can_edit ? (
                  <button type="button" className="vox-sbm-icon-btn" onClick={() => setDescribing({ id: c.id, text: c.description || '' })} aria-label="Edit description" title="Edit description">
                    ✎
                  </button>
                ) : null}
              </div>
            ) : null}
            {!c.can_edit && (c.is_server || c.visibility === 'server') ? <div className="vox-sbm-hint">Only members with the Manage server sounds permission on {c.server_name || 'this server'} can change this pack. Server owners manage it in Server settings → Soundboard.</div> : null}
            {!c.can_edit && !(c.is_server || c.visibility === 'server') ? <div className="vox-sbm-hint">Installed from {c.creator || 'someone'}. It stays linked to their pack, so you get their updates. Copy sounds into one of your packs to change them.</div> : null}
          </div>
        </div>

        {c.updated_since_install ? (
          <div className="vox-sbm-banner">
            This pack has changed since you installed it.
            <button type="button" className="coms-mini-btn tiny" onClick={() => act(() => comsApiPost(`/sounds/installs/${c.id}/ack`, {}), 'Failed.')}>Mark reviewed</button>
          </div>
        ) : null}

        <div className="vox-sbm-actions">
          {c.can_edit ? (
            <button type="button" className="coms-mini-btn primary" disabled={count >= (c.max_sounds || 60)} onClick={() => setEditor({})}>+ Add sound</button>
          ) : null}
          {c.can_edit && !(c.is_server || c.visibility === 'server') ? (
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              Who can use it
              <select
                value={c.published ? 'market' : c.visibility === 'public' ? 'unlisted' : c.visibility}
                disabled={busy}
                aria-label="Who can use this pack"
                style={{ ...field, padding: '4px 8px' }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'market') setPublishing({ id: c.id, category: c.category || 'other', tags: (c.tags || []).join(' ') });
                  else act(() => comsApiPatch(`/sounds/collections/${c.id}`, { visibility: v }), 'Update failed.');
                }}
              >
                <option value="private">Only me</option>
                <option value="friends">Friends (with the share code)</option>
                <option value="unlisted">Anyone with the share code</option>
                <option value="market">Marketplace (free)</option>
              </select>
            </label>
          ) : null}
          {c.can_edit && c.share_code && ['friends', 'unlisted', 'public'].includes(c.visibility) ? (
            <>
              <button type="button" className="coms-mini-btn" title={c.share_code} onClick={() => navigator.clipboard && navigator.clipboard.writeText(c.share_code).then(() => setNotice('Share code copied.')).catch(() => setErr('Could not copy. The code is: ' + c.share_code))}>
                Copy share code
              </button>
              <button type="button" className="coms-mini-btn" title="Old codes stop working" onClick={() => act(() => comsApiPost(`/sounds/collections/${c.id}/share-code`, {}).then(() => setNotice('New share code created.')), 'Could not create a new code.')}>
                New code
              </button>
            </>
          ) : null}
          <button type="button" className="coms-mini-btn" onClick={() => exportPack(c)}>Export</button>
          {c.can_edit && c.icon_url ? (
            <button type="button" className="coms-mini-btn" onClick={() => act(() => comsApiDelete(`/sounds/collections/${c.id}/icon`), 'Could not remove the artwork.')}>Remove artwork</button>
          ) : null}
          {srv && srv.can_manage && c.visibility === 'server' ? (
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!srv.enabled} onChange={(e) => act(() => comsApiPut(`/sounds/server-settings/${encodeURIComponent(srv.key)}`, { enabled: e.target.checked }), 'Could not change the server setting.')} />
              Soundboard allowed on {srv.name}
            </label>
          ) : null}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {c.can_edit || c.installed ? (
              confirm === `pack:${c.id}` ? (
                <>
                  <span style={{ fontSize: 12, alignSelf: 'center' }}>{c.can_edit ? `Delete “${c.name}” and its ${count} sound${count === 1 ? '' : 's'}?` : 'Remove this pack?'}</span>
                  <button type="button" className="coms-mini-btn vox-sbm-danger" disabled={busy} onClick={() => act(() => comsApiDelete(c.can_edit ? `/sounds/collections/${c.id}` : `/sounds/installs/${c.id}`), c.can_edit ? 'Delete failed.' : 'Remove failed.')}>
                    {c.can_edit ? 'Delete' : 'Remove'}
                  </button>
                  <button type="button" className="coms-mini-btn" onClick={() => setConfirm('')}>Cancel</button>
                </>
              ) : (
                <button type="button" className="coms-mini-btn vox-sbm-danger-outline" onClick={() => setConfirm(`pack:${c.id}`)}>
                  {c.can_edit ? 'Delete pack' : 'Remove pack'}
                </button>
              )
            ) : null}
          </span>
        </div>

        {publishing && publishing.id === c.id ? (
          <div className="vox-sbm-create" role="group" aria-label="Publish to the Marketplace">
            <div style={{ flexBasis: '100%', fontSize: 13, fontWeight: 800 }}>Publish “{c.name}” in the Marketplace (free)</div>
            <select value={publishing.category} onChange={(e) => setPublishing({ ...publishing, category: e.target.value })} aria-label="Category" style={field}>
              {categories.map((k) => (
                <option key={k} value={k}>
                  {CATEGORY_LABEL[k] || k}
                </option>
              ))}
            </select>
            <input value={publishing.tags} onChange={(e) => setPublishing({ ...publishing, tags: e.target.value })} placeholder="Tags (space separated)" aria-label="Tags" style={{ ...field, flex: '1 1 180px' }} />
            <button type="button" className="coms-mini-btn primary" disabled={busy || !count} onClick={() => act(() => comsApiPost(`/sounds/collections/${c.id}/publish`, { category: publishing.category, tags: publishing.tags }).then(() => { setPublishing(null); setNotice('Published in the Marketplace.'); }), 'Could not publish the pack.')}>
              Publish
            </button>
            <button type="button" className="coms-mini-btn" onClick={() => setPublishing(null)}>Cancel</button>
            <div className="vox-sbm-hint" style={{ flexBasis: '100%' }}>
              Anyone can find, preview and install it. Installs stay linked to your pack, so they get your updates; you stay its owner. Paid packs are not available yet.
            </div>
          </div>
        ) : null}
        {c.published && c.can_edit ? (
          <div className="vox-sbm-actions">
            <button type="button" className="coms-mini-btn" onClick={() => act(() => comsApiPost(`/sounds/collections/${c.id}/unpublish`, {}).then(() => setNotice('Removed from the Marketplace. People who installed it keep it.')), 'Could not unpublish.')}>
              Remove from the Marketplace
            </button>
          </div>
        ) : null}

        {editor ? (
          <SoundClipEditor key={editor.replace ? `r:${editor.replace.sound.id}` : 'new'} collections={collections} defaultTarget={String(c.id)} replace={editor.replace || null} onCancel={() => setEditor(null)} onSaved={() => { setEditor(null); load(); setNotice('Sound saved.'); }} />
        ) : null}

        <div className="vox-sbm-sounds">
          {list.length ? list.map((s) => soundRow('col', s, c)) : <div className="vox-sbm-empty">{q ? 'No sounds match your search.' : c.can_edit ? 'This pack is empty. Add a sound from a file or record one.' : 'This pack has no sounds yet.'}</div>}
        </div>
      </>
    );
  }

  return (
    <div className="vox-sbm" style={{ marginTop: 14 }}>
      <div className="vox-sbm-toolbar">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search packs and sounds" aria-label="Search packs and sounds" style={{ ...field, flex: '1 1 180px', minWidth: 0 }} />
        <button type="button" className="coms-mini-btn primary" onClick={() => setCreating((v) => !v)} aria-expanded={creating}>
          + New pack
        </button>
        <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && code.trim() && previewCode()} placeholder="Share code" aria-label="Share code" style={{ ...field, width: 130 }} />
        <button type="button" className="coms-mini-btn" disabled={!code.trim()} onClick={previewCode}>Install…</button>
        <label className="coms-mini-btn" style={{ cursor: 'pointer' }}>
          Import file…
          <input type="file" accept=".json,application/json" style={{ display: 'none' }} onChange={(e) => { importFile(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </label>
      </div>

      {creating ? (
        <div className="vox-sbm-create">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newName.trim().length >= 2 && createPack()}
            placeholder="Pack name"
            aria-label="New pack name"
            maxLength={40}
            autoFocus
            style={{ ...field, flex: '1 1 180px' }}
          />
          <select value={newWhere} onChange={(e) => setNewWhere(e.target.value)} aria-label="Who can use it" style={field}>
            <option value="private">Only me</option>
            <option value="friends">Friends (with the share code)</option>
            {manageable.map((s) => (
              <option key={s.key} value={`server:${s.key}`}>
                Server: {s.name}
              </option>
            ))}
          </select>
          <button type="button" className="coms-mini-btn primary" disabled={busy || newName.trim().length < 2} onClick={createPack}>Create</button>
          <button type="button" className="coms-mini-btn" onClick={() => setCreating(false)}>Cancel</button>
          <div className="vox-sbm-hint" style={{ flexBasis: '100%' }}>
            {manageable.length ? 'Server packs can be played by everyone on that server.' : 'To create a server pack you need the Manage soundboard permission on that server.'}
          </div>
        </div>
      ) : null}

      {personalQuota ? (
        <div className="vox-sbm-hint" style={{ marginTop: 6 }}>
          {personalQuota.used} / {personalQuota.limit} sounds in your personal packs · clips on this device do not count
        </div>
      ) : null}
      {err ? <div className="coms-form-error" role="alert">{err}</div> : null}
      {notice ? <div className="vox-sbm-notice" role="status">{notice}</div> : null}

      <div className="vox-sbm-layout">
        <nav className="vox-sbm-list" aria-label="Sound packs">
          {section('On this device', [packButton(LOCAL, 'This device', `${localSounds.length} sound${localSounds.length === 1 ? '' : 's'} · Only here`, <PackArt emoji="💻" />)])}
          {section('My packs', groups.mine.filter(packMatch).map(packItem), loaded && !q ? 'No packs yet. Create one with “New pack”.' : '')}
          {groups.servers.map((g) => {
            const items = g.packs.filter(packMatch);
            return items.length ? <React.Fragment key={g.key}>{section(g.name, items.map(packItem))}</React.Fragment> : null;
          })}
          {groups.installed.filter(packMatch).length ? section('Installed from others', groups.installed.filter(packMatch).map(packItem)) : null}
        </nav>
        <section className="vox-sbm-detail">{detail()}</section>
      </div>

      {trust ? <TrustDialog title={trust.title} info={trust.info} busy={busy} onCancel={() => setTrust(null)} onConfirm={runTrust} /> : null}
    </div>
  );
}
